import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaTimes, FaFile } from 'react-icons/fa';
import { formatFileSize } from '../../utils/uploadValidator';
import { useFileUpload } from '../../hooks/useFileUpload';
import { notify } from '../CustomToast';
import projectService from '../../services/projectService';
import './TaskAttachmentsUploader.css';

/**
 * TaskAttachmentsUploader - Edit mode file upload
 *
 * Architecture for Edit Mode:
 * 1. Load existing attachments on mount
 * 2. User selects files → immediately uploaded to /temp-upload (onChange)
 * 3. Each file tracked with status: uploading, attached, error
 * 4. After temp upload completes, automatically attach to task via attachFileToTask
 * 5. Show upload + attachment progress to user
 * 6. Allow deletion of existing attachments
 */
const TaskAttachmentsUploader = ({ taskId, onAttachmentsAdded, onUploadStatusChange, disabled = false }) => {
    const fileInputRef = useRef(null);
    const { uploadedFiles, uploadFiles, removeFile, clearAll } = useFileUpload();
    const [attachingFiles, setAttachingFiles] = useState({});
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);

    // Load existing attachments on mount
    useEffect(() => {
        if (taskId) {
            loadExistingAttachments();
        }
    }, [taskId]);

    // Notify parent of upload status changes
    useEffect(() => {
        if (onUploadStatusChange) {
            const isUploading = uploadedFiles.some(file => file.status === 'uploading') ||
                               Object.keys(attachingFiles).length > 0;
            onUploadStatusChange(isUploading);
        }
    }, [uploadedFiles, attachingFiles, onUploadStatusChange]);

    const loadExistingAttachments = async () => {
        try {
            setLoadingAttachments(true);
            const response = await projectService.getAttachments(taskId);
            const attachmentsPayload = response?.data?.attachments ?? response?.attachments;
            const attachments = Array.isArray(attachmentsPayload)
                ? attachmentsPayload
                : Array.isArray(attachmentsPayload?.data)
                    ? attachmentsPayload.data
                    : [];
            const normalizedAttachments = attachments
                .map((attachment) => attachment?.data ?? attachment)
                .filter((attachment) => attachment && typeof attachment === 'object');
            setExistingAttachments(normalizedAttachments);
        } catch (error) {
            console.error('Failed to load attachments:', error);
            // Don't notify error - attachments are optional
        } finally {
            setLoadingAttachments(false);
        }
    };

    const deleteExistingAttachment = async (attachmentId) => {
        try {
            await projectService.deleteAttachment(taskId, attachmentId);
            setExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
            notify('success', 'Attachment removed');
        } catch (error) {
            console.error('Failed to delete attachment:', error);
            notify('error', `Failed to remove attachment: ${error.message || 'Unknown error'}`);
        }
    };

    const handleFileSelect = async (e) => {
        const files = e.target.files;
        if (!files || !taskId) return;

        // Upload files immediately on selection
        const uploadIds = await uploadFiles(files);

        // Now attach each uploaded file to the task
        const attachedFiles = [];

        for (const uploadId of uploadIds) {
            try {
                setAttachingFiles((prev) => ({
                    ...prev,
                    [uploadId]: { status: 'attaching' },
                }));

                const attachmentResponse = await projectService.attachFileToTask(taskId, uploadId);
                const newAttachment = attachmentResponse?.data?.attachment ?? attachmentResponse?.attachment;

                if (newAttachment) {
                    attachedFiles.push(newAttachment);
                }
            } catch (error) {
                console.error(`Failed to attach file with ID ${uploadId}:`, error);
                notify('error', `Failed to attach file: ${error.message || 'Unknown error'}`);
            } finally {
                setAttachingFiles((prev) => {
                    const updated = { ...prev };
                    delete updated[uploadId];
                    return updated;
                });
            }
        }

        // Notify parent of newly attached files
        if (attachedFiles.length > 0) {
            const normalizedAttachedFiles = attachedFiles
                .map((attachment) => attachment?.data ?? attachment)
                .filter((attachment) => attachment && typeof attachment === 'object');

            // Keep new files visible immediately in this modal session
            setExistingAttachments((prev) => {
                const prevIds = new Set(prev.map((attachment) => attachment?.id).filter(Boolean));
                const uniqueNew = normalizedAttachedFiles.filter(
                    (attachment) => attachment?.id && !prevIds.has(attachment.id)
                );
                return [...uniqueNew, ...prev];
            });

            notify('success', `${attachedFiles.length} file(s) attached successfully`);
            if (onAttachmentsAdded) {
                onAttachmentsAdded(normalizedAttachedFiles);
            }
            clearAll();

            // Re-sync with backend response to keep canonical order/data
            loadExistingAttachments();
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const hasUploadedFiles = uploadedFiles.length > 0;

    return (
        <div className="task-attachments-uploader">
            <div className="uploader-header">
                <label htmlFor="task-file-input">Attachments</label>
                <button
                    type="button"
                    className="btn-add-attachment"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                >
                    <FaPlus size={14} /> Add Files
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    id="task-file-input"
                    multiple
                    onChange={handleFileSelect}
                    disabled={disabled}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
                <div className="existing-attachments-section">
                    <h4 className="existing-attachments-title">Current Attachments ({existingAttachments.length})</h4>
                    <div className="selected-files-list">
                        {existingAttachments.map((attachment) => (
                            <div key={attachment.id} className="file-item existing">
                                <div className="file-icon" style={{ backgroundColor: '#16a34a' }}>
                                    <FaFile size={16} color="#fff" />
                                </div>

                                <div className="file-info">
                                    <div className="file-name" title={attachment.upload?.original_filename}>
                                        {attachment.upload?.original_filename}
                                    </div>
                                    <div className="file-size">
                                        {formatFileSize(attachment.upload?.file_size)}
                                    </div>
                                </div>

                                <div className="file-uploaded">✓ Attached</div>

                                <button
                                    type="button"
                                    className="btn-remove-file"
                                    onClick={() => deleteExistingAttachment(attachment.id)}
                                    disabled={disabled}
                                    title="Remove attachment"
                                >
                                    <FaTimes size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hasUploadedFiles && (
                <div className="selected-files-list">
                    {uploadedFiles.map((fileData) => (
                        <div key={fileData.id} className={`file-item ${fileData.status}`}>
                            <div className="file-icon" style={{ backgroundColor: '#0c5097' }}>
                                <FaFile size={16} color="#fff" />
                            </div>

                            <div className="file-info">
                                <div className="file-name" title={fileData.name}>
                                    {fileData.name}
                                </div>
                                <div className="file-size">{formatFileSize(fileData.size)}</div>
                            </div>

                            {fileData.status === 'uploading' && (
                                <div className="file-upload-progress">
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{
                                                width: `${fileData.progress}%`,
                                            }}
                                        />
                                    </div>
                                    <div className="progress-text">{fileData.progress}%</div>
                                </div>
                            )}

                            {attachingFiles[fileData.uploadId] && (
                                <div className="file-upload-progress">
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{
                                                width: '100%',
                                            }}
                                        />
                                    </div>
                                    <div className="progress-text">Attaching...</div>
                                </div>
                            )}

                            {fileData.error && (
                                <div className="file-error">{fileData.error}</div>
                            )}

                            {fileData.status === 'uploaded' && !attachingFiles[fileData.uploadId] && (
                                <div className="file-uploaded">✓ Attached</div>
                            )}

                            {fileData.status !== 'uploading' && !attachingFiles[fileData.uploadId] && !fileData.error && (
                                <button
                                    type="button"
                                    className="btn-remove-file"
                                    onClick={() => removeFile(fileData.id, fileData.uploadId)}
                                    disabled={disabled}
                                    title="Remove file"
                                >
                                    <FaTimes size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TaskAttachmentsUploader;
